/**
 * Expandable Text Custom Element
 *
 * Handles expand/collapse functionality with smooth CSS transitions
 * Preserves HTML formatting when truncating
 */
export default class ExpandableText extends HTMLElement {
  connectedCallback() {
    const shouldTruncate = this.getAttribute('data-should-truncate') === 'true';

    if (!shouldTruncate) {
      return; // No truncation needed
    }

    this.content = this.querySelector('.expandable-text__content');
    this.toggleButton = this.querySelector('.expandable-text__toggle');
    this.toggleLabel = this.querySelector('.expandable-text__toggle-label');

    this.readMoreLabel = this.getAttribute('data-read-more-label') || 'Read more...';
    this.readLessLabel = this.getAttribute('data-read-less-label') || 'Read less...';
    this.truncateWords = parseInt(this.getAttribute('data-truncate-words') || '50', 10);

    this.isExpanded = false;

    // Store the toggle button HTML
    this.toggleButtonHTML = this.toggleButton.outerHTML;

    // Remove button temporarily and store original text content
    this.toggleButton.remove();
    this.fullTextContent = this.content.innerHTML;

    // Store full content with button for later
    this.fullContent = this.fullTextContent + ' ' + this.toggleButtonHTML;

    // Measure full height
    const fullHeight = this.content.scrollHeight;
    this.style.setProperty('--expanded-height', `${fullHeight}px`);

    // Truncate content while preserving HTML and append button
    this.truncatedContent = this.truncateHTML(this.fullTextContent, this.truncateWords) + ' ' + this.toggleButtonHTML;
    this.content.innerHTML = this.truncatedContent;

    // Re-attach button reference
    this.toggleButton = this.querySelector('.expandable-text__toggle');
    this.toggleLabel = this.querySelector('.expandable-text__toggle-label');

    // Measure truncated height after a small delay to ensure rendering
    requestAnimationFrame(() => {
      const truncatedHeight = this.content.scrollHeight;
      this.style.setProperty('--collapsed-height', `${truncatedHeight}px`);
    });

    // Use event delegation on the component itself (single listener, never needs reattachment)
    this.addEventListener('click', (e) => {
      if (e.target.closest('.expandable-text__toggle')) {
        this.toggle();
      }
    });
  }

  /**
   * Truncate HTML content to a specific word count while preserving tags
   * @param {string} html - HTML string to truncate
   * @param {number} wordLimit - Maximum number of words
   * @returns {string} Truncated HTML with ellipsis
   */
  truncateHTML(html, wordLimit) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    let wordCount = 0;
    let truncated = false;

    const truncateNode = (node) => {
      if (truncated) return null;

      if (node.nodeType === Node.TEXT_NODE) {
        const words = node.textContent.trim().split(/\s+/).filter(w => w.length > 0);

        if (wordCount + words.length <= wordLimit) {
          wordCount += words.length;
          return node.cloneNode(true);
        } else {
          const remainingWords = wordLimit - wordCount;
          const truncatedText = words.slice(0, remainingWords).join(' ') + '...';
          wordCount = wordLimit;
          truncated = true;
          return document.createTextNode(truncatedText);
        }
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const clone = node.cloneNode(false);

        for (let child of node.childNodes) {
          if (truncated) break;
          const truncatedChild = truncateNode(child);
          if (truncatedChild) {
            clone.appendChild(truncatedChild);
          }
        }

        return clone;
      }

      return null;
    };

    const result = document.createElement('div');
    for (let child of tempDiv.childNodes) {
      if (truncated) break;
      const truncatedChild = truncateNode(child);
      if (truncatedChild) {
        result.appendChild(truncatedChild);
      }
    }

    return result.innerHTML;
  }

  toggle() {
    if (this.isExpanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  expand() {
    this.isExpanded = true;

    // Create full content with "Read less" button
    const readLessButtonHTML = this.toggleButtonHTML.replace(this.readMoreLabel, this.readLessLabel);
    const fullContentWithButton = this.fullTextContent + ' ' + readLessButtonHTML;

    // Restore full content with button
    this.content.innerHTML = fullContentWithButton;

    // Re-attach button reference (event delegation handles clicks automatically)
    this.toggleButton = this.querySelector('.expandable-text__toggle');
    this.toggleLabel = this.querySelector('.expandable-text__toggle-label');

    // Remeasure full height (in case of dynamic content)
    requestAnimationFrame(() => {
      const fullHeight = this.content.scrollHeight;
      this.style.setProperty('--expanded-height', `${fullHeight}px`);
    });

    // Toggle classes for CSS transition
    this.content.classList.remove('expandable-text__content--collapsed');
    this.content.classList.add('expandable-text__content--expanded');
  }

  collapse() {
    this.isExpanded = false;

    // Restore truncated content with "Read more" button
    this.content.innerHTML = this.truncatedContent;

    // Re-attach button reference (event delegation handles clicks automatically)
    this.toggleButton = this.querySelector('.expandable-text__toggle');
    this.toggleLabel = this.querySelector('.expandable-text__toggle-label');

    // Remeasure truncated height
    requestAnimationFrame(() => {
      const truncatedHeight = this.content.scrollHeight;
      this.style.setProperty('--collapsed-height', `${truncatedHeight}px`);
    });

    // Toggle classes for CSS transition
    this.content.classList.remove('expandable-text__content--expanded');
    this.content.classList.add('expandable-text__content--collapsed');

    // Scroll to element if it's above viewport after transition starts
    // Use setTimeout to allow CSS transition to progress before checking position
    setTimeout(() => {
      requestAnimationFrame(() => {
        const rect = this.getBoundingClientRect();
        if (rect.top < 0) {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }, 100);
  }
}

customElements.define('expandable-text', ExpandableText);
